/** 一键吃鸡（Positioning）状态、PUBG 云端预设与设备通信桥接。 */
import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { hidManager } from '@/api/device/hid'
import {
  buildEqLoginStatusCommand,
  buildEqModeCommand,
  buildPositioningCurveCommands,
  buildPositioningSliderCommands,
  bytesToHexString,
  type EqDataPoint,
} from '@/api/device/commands'
import {
  addPubgPreset,
  findPubgById,
  getPubgList,
  pinPubgPreset,
  removePubgPreset,
  renamePubgPreset,
  resetPubgPreset,
  setLastPubgPreset,
  updatePubgPoint,
  type PubgPresetData,
} from '@/api/chiji'
import { useUserStore } from './user'
import { useDeviceStore } from './device'

export type ChijiPresetType = 'system' | 'custom'
export interface ChijiSettings { footsteps: number; gunshots: number }
export interface ChijiPreset {
  id: string
  name: string
  type: ChijiPresetType
  settings: ChijiSettings
  datapoint: EqDataPoint[]
  isPinned?: boolean
  createdAt?: number
}

type PositioningSound = keyof ChijiSettings

/**
 * Positioning 的 14 段频点来自页面标准 14 段，并按需求替换：
 * 120/250/500 -> 200/300/400，4K -> 3K，其余频点保留。
 * 枪声：200/300/400/800Hz；脚步声：2K/3K。
 */
export const POSITIONING_FREQUENCIES = [
  '20HZ', '60HZ', '200HZ', '300HZ', '400HZ', '800HZ', '1K',
  '2K', '3K', '6K', '8K', '10K', '12K', '20K',
] as const

const GUNSHOT_FIDS = [3, 4, 5, 6] as const
const FOOTSTEP_FIDS = [8, 9] as const
const DEFAULT_Q = '0.75'
const MAX_CUSTOM = 20
const DEFAULT_SYSTEM_SETTINGS: ChijiSettings = { footsteps: 2, gunshots: -3 }
const WRITE_GUARD_MS = 1200
const API_POINT_DEBOUNCE_MS = 350

function clampGain(value: unknown): number {
  const number = Number(value)
  if (!Number.isFinite(number)) return 0
  return Math.max(-3, Math.min(3, Math.round(number)))
}

function cloneSettings(value?: Partial<ChijiSettings>): ChijiSettings {
  return {
    footsteps: clampGain(value?.footsteps ?? DEFAULT_SYSTEM_SETTINGS.footsteps),
    gunshots: clampGain(value?.gunshots ?? DEFAULT_SYSTEM_SETTINGS.gunshots),
  }
}

function createCurve(value: ChijiSettings): EqDataPoint[] {
  const gunGain = clampGain(value.gunshots)
  const footGain = clampGain(value.footsteps)
  return POSITIONING_FREQUENCIES.map((freq, index) => {
    const fid = index + 1
    let gain = 0
    if ((GUNSHOT_FIDS as readonly number[]).includes(fid)) gain = gunGain
    if ((FOOTSTEP_FIDS as readonly number[]).includes(fid)) gain = footGain
    return { fid, freq, gain, q: DEFAULT_Q }
  })
}

function extractSettings(points: EqDataPoint[] | undefined): ChijiSettings {
  const source = Array.isArray(points) ? points : []
  const byFid = new Map<number, EqDataPoint>()
  source.forEach((point, index) => byFid.set(Number(point.fid) || index + 1, point))

  // PUBG 服务端数据是一条 14 段曲线。滑动条只读取对应目标频点的 gain。
  const gunPoint = GUNSHOT_FIDS.map(fid => byFid.get(fid)).find(Boolean)
  const footPoint = FOOTSTEP_FIDS.map(fid => byFid.get(fid)).find(Boolean)
  return {
    gunshots: clampGain(gunPoint?.gain ?? 0),
    footsteps: clampGain(footPoint?.gain ?? 0),
  }
}

function createPreset(
  id: string,
  name: string,
  type: ChijiPresetType,
  value: Partial<ChijiSettings>,
  pinned = false,
  createdAt?: number,
): ChijiPreset {
  const normalizedSettings = cloneSettings(value)
  return {
    id,
    name,
    type,
    settings: normalizedSettings,
    datapoint: createCurve(normalizedSettings),
    isPinned: pinned,
    createdAt,
  }
}

function normalizeRemotePreset(source: PubgPresetData, type: ChijiPresetType, pinned = false): ChijiPreset {
  return createPreset(
    String(source.id),
    // 一键吃鸡的系统默认标签无论登录/未登录都统一显示英文 Preset。
    // 这里只做前端标准化，不修改服务器保存的原始名称。
    type === 'system' ? 'Preset' : String(source.name || 'Custom'),
    type,
    extractSettings(source.datapoint),
    pinned,
    source.createdAt,
  )
}

function detectSystemPresetIndex(presets: PubgPresetData[]): number {
  const explicitSystem = presets.findIndex(item => item.isCustom === false)
  if (explicitSystem >= 0) return explicitSystem

  const knownId = presets.findIndex(item => /^(initial|preset)$/i.test(String(item.id || '').trim()))
  if (knownId >= 0) return knownId

  const knownName = presets.findIndex(item => /^preset$/i.test(String(item.name || '').trim()))
  if (knownName >= 0) return knownName

  // PUBG 当前只有一个系统 Preset；接口未返回 isCustom 时，以首条作为系统预设兜底。
  return presets.length ? 0 : -1
}

function showGlobalLightTip(message: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<string>('app-light-tip', { detail: message }))
}

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function packetHex(packet: Uint8Array): string {
  return bytesToHexString(packet)
}

export const useChijiStore = defineStore('chiji', () => {
  const userStore = useUserStore()
  const deviceStore = useDeviceStore()
  userStore.loadFromStorage()

  const enabled = ref(false)
  const hasEverOpened = ref(false)
  const currentPresetId = ref('initial')
  const appliedPresetId = ref<string | null>(null)
  const pinnedPresetId = ref<string | null>(null)
  const customPresets = ref<ChijiPreset[]>([])
  const systemPreset = ref<ChijiPreset>(createPreset('initial', 'Preset', 'system', DEFAULT_SYSTEM_SETTINGS))
  const orderedIds = ref<string[]>(['initial'])
  const remoteLoaded = ref(false)
  const remoteLoading = ref(false)

  const sourceGeneration = ref(0)
  const selectionVersion = ref(0)
  const mutationVersion = ref(0)
  const systemOriginSettings = ref<ChijiSettings>(cloneSettings(DEFAULT_SYSTEM_SETTINGS))

  let sourceContextKey = ''
  let lastHardwareSyncKey = ''
  let loggedInHardwareEnabledCaptured = false
  let localActionGuardUntil = 0
  let deviceQueue = Promise.resolve()
  let remoteReloadTimer: ReturnType<typeof setTimeout> | null = null
  const pointSyncTimers = new Map<string, ReturnType<typeof setTimeout>>()
  // 标签点击属于离散操作，不再延迟 300ms 后再写 setLast。
  // 串行队列保证快速连续点击时，只有最新一次选择最终生效，避免旧请求晚返回覆盖新标签。
  let lastPresetWriteQueue: Promise<void> = Promise.resolve()
  let lastPresetWriteSerial = 0
  const pendingAddPromises = new Map<string, Promise<boolean>>()
  const presetIdAliases = new Map<string, string>()

  function isLoggedIn(): boolean {
    return userStore.isLoggedIn && !!userStore.uuid
  }

  function getUuid(): string {
    return userStore.uuid || ''
  }

  function getLinktype(): string {
    const connection = deviceStore.connectionType || hidManager.getConnectionType()
    return connection === '2.4G' ? '0' : '1'
  }

  function resolvePresetId(id: string): string {
    let current = id
    const visited = new Set<string>()
    while (presetIdAliases.has(current) && !visited.has(current)) {
      visited.add(current)
      current = presetIdAliases.get(current) || current
    }
    return current
  }

  function applyPinnedFlags(): void {
    systemPreset.value.isPinned = pinnedPresetId.value === systemPreset.value.id
    customPresets.value.forEach(item => { item.isPinned = pinnedPresetId.value === item.id })
  }

  const allPresets = computed(() => {
    applyPinnedFlags()
    const map = new Map<string, ChijiPreset>([[systemPreset.value.id, systemPreset.value]])
    customPresets.value.forEach(item => map.set(item.id, item))
    const ids = orderedIds.value.filter((id, index, list) => list.indexOf(id) === index)
    if (!ids.includes(systemPreset.value.id)) ids.unshift(systemPreset.value.id)
    customPresets.value.forEach(item => { if (!ids.includes(item.id)) ids.push(item.id) })
    return ids.map(id => map.get(id)).filter(Boolean) as ChijiPreset[]
  })

  const currentPreset = computed(() => (
    allPresets.value.find(item => item.id === currentPresetId.value)
    || systemPreset.value
    || allPresets.value[0]
  ))
  const isCustomPreset = computed(() => currentPreset.value?.type === 'custom')
  const tagValue = computed(() => Math.max(0, allPresets.value.findIndex(item => item.id === currentPresetId.value)))

  function updatePresetCurve(item: ChijiPreset): void {
    item.settings = cloneSettings(item.settings)
    item.datapoint = createCurve(item.settings)
  }

  function markSelection(): void {
    selectionVersion.value += 1
  }

  function markMutation(): void {
    mutationVersion.value += 1
  }

  function enqueueDevice(task: () => Promise<void>): Promise<void> {
    deviceQueue = deviceQueue.then(task).catch(error => {
      console.error('[PUBG][HID] 设备命令执行失败:', error)
    })
    return deviceQueue
  }

  async function sendLoginStatus(): Promise<void> {
    if (!hidManager.isConnected()) return
    const packet = buildEqLoginStatusCommand(isLoggedIn())
    console.log('[PUBG][HID] 0x13 登录状态', {
      loginStatus: isLoggedIn() ? 1 : 0,
      packet: packetHex(packet),
    })
    await hidManager.sendData(packet, false)
  }

  async function sendProtocol(): Promise<void> {
    if (!hidManager.isConnected()) return
    const eqStore = await import('./eq').then(module => module.useEqStore())
    const mode = eqStore.appliedMode || eqStore.activeMode
    const modeValue = ({ game: 0, movie: 1, music: 2 } as Record<string, number>)[mode] ?? 0
    const eqIndex = eqStore.isEnabled && eqStore.appliedPresetId
      ? Math.max(0, eqStore.currentModeState.systemPresets.concat(eqStore.currentModeState.customPresets)
        .findIndex(item => item.id === eqStore.appliedPresetId))
      : 0
    const packet = buildEqModeCommand(eqStore.isEnabled, modeValue, eqIndex, enabled.value, tagValue.value)

    await sendLoginStatus()
    console.log('[PUBG][HID] 0x14 开关/标签', {
      eqEnabled: eqStore.isEnabled ? 1 : 0,
      eqMode: modeValue,
      eqTag: eqIndex,
      positioningEnabled: enabled.value ? 1 : 0,
      positioningTag: tagValue.value,
      presetId: currentPresetId.value,
      packet: packetHex(packet),
    })
    await hidManager.sendData(packet, false)
  }

  function pointsForSubModule(item: ChijiPreset, subModule: number): EqDataPoint[] {
    if (subModule === 0x15) return item.datapoint.slice(0, 4)
    if (subModule === 0x16) return item.datapoint.slice(4, 7)
    if (subModule === 0x17) return item.datapoint.slice(7, 11)
    if (subModule === 0x18) return item.datapoint.slice(11, 14)
    return []
  }

  async function sendCurve(item = currentPreset.value, sound: PositioningSound | 'all' = 'all'): Promise<void> {
    if (!item || !hidManager.isConnected()) return
    updatePresetCurve(item)

    const commands = sound === 'all'
      ? buildPositioningCurveCommands(item.datapoint)
      : buildPositioningSliderCommands(item.datapoint, sound)

    await sendLoginStatus()
    console.log(`[PUBG][HID] ${sound === 'all' ? '应用标签：脚步声 + 枪声完整 14 段' : `${sound === 'footsteps' ? '脚步声' : '枪声'}滑动条局部分包`}`, {
      presetId: item.id,
      presetName: item.name,
      footsteps: item.settings.footsteps,
      gunshots: item.settings.gunshots,
      curve: item.datapoint.map(point => ({ fid: point.fid, freq: point.freq, gain: point.gain, q: point.q })),
    })

    for (let index = 0; index < commands.length; index += 1) {
      const command = commands[index]
      const subModule = command[4]
      console.log(`[PUBG][HID] -> 0x${subModule.toString(16).toUpperCase()} 数据包`, {
        points: pointsForSubModule(item, subModule).map(point => ({ fid: point.fid, freq: point.freq, gain: point.gain, q: point.q })),
        packet: packetHex(command),
      })
      await hidManager.sendData(command, false)
      if (index < commands.length - 1) await delay(30)
    }
  }

  function scheduleRemoteReload(delayMs = 900): void {
    if (!isLoggedIn()) return
    if (remoteReloadTimer) clearTimeout(remoteReloadTimer)
    remoteReloadTimer = setTimeout(() => {
      remoteReloadTimer = null
      void loadFromRemote(true)
    }, delayMs)
  }

  async function awaitPendingAdd(id: string): Promise<boolean> {
    const originalId = id
    const pending = pendingAddPromises.get(originalId)
    if (!pending) return true
    try {
      return await pending
    } catch {
      return false
    }
  }

  async function confirmLastPresetReadBack(
    presetId: string,
    requestUuid: string,
    requestLinktype: string,
    requestGeneration: number,
    requestSerial: number,
  ): Promise<boolean> {
    // 后端 /pubg/list 存在短暂最终一致性：setLast 成功后的第一次 list 可能仍返回旧 lastPreset，
    // 第二次读取才刷新为最新值。这里由前端自动完成回读，用户只需要点击一次标签。
    const retryDelays = [120, 260]
    for (const waitMs of retryDelays) {
      await delay(waitMs)
      if (
        !isLoggedIn()
        || requestGeneration !== sourceGeneration.value
        || requestSerial !== lastPresetWriteSerial
        || getUuid() !== requestUuid
        || getLinktype() !== requestLinktype
      ) return false

      try {
        const response = await getPubgList(requestUuid, requestLinktype)
        if (Number(response.code) === 1 && String(response.data?.lastPreset || '') === presetId) return true
      } catch (error) {
        console.warn('[PUBG][API] 回读最后使用标签失败，继续后台校正:', error)
      }
    }
    return false
  }

  function scheduleSetLastPreset(id: string): void {
    if (!isLoggedIn()) return

    const requestGeneration = sourceGeneration.value
    const requestUuid = getUuid()
    const requestLinktype = getLinktype()
    const originalId = id
    const requestSerial = ++lastPresetWriteSerial

    const writeTask = lastPresetWriteQueue
      .catch(() => undefined)
      .then(async () => {
        // 队列真正执行前若用户已经点了更新标签，旧任务直接跳过。
        if (
          !isLoggedIn()
          || requestGeneration !== sourceGeneration.value
          || requestSerial !== lastPresetWriteSerial
          || getUuid() !== requestUuid
          || getLinktype() !== requestLinktype
        ) return

        const addSucceeded = await awaitPendingAdd(originalId)
        if (
          !addSucceeded
          || !isLoggedIn()
          || requestGeneration !== sourceGeneration.value
          || requestSerial !== lastPresetWriteSerial
          || getUuid() !== requestUuid
          || getLinktype() !== requestLinktype
        ) return

        const resolvedId = resolvePresetId(originalId)
        if (!allPresets.value.some(item => item.id === resolvedId)) return

        try {
          const response = await setLastPubgPreset(requestUuid, resolvedId, requestLinktype)
          if (Number(response.code) !== 1) {
            console.warn('[PUBG][API] setLast 返回失败:', response)
            return
          }

          // setLast 返回期间如果用户又点击了别的标签，不再让旧标签触发后续回读/刷新。
          if (
            requestGeneration !== sourceGeneration.value
            || requestSerial !== lastPresetWriteSerial
            || getUuid() !== requestUuid
            || getLinktype() !== requestLinktype
          ) return

          const confirmed = await confirmLastPresetReadBack(
            resolvedId,
            requestUuid,
            requestLinktype,
            requestGeneration,
            requestSerial,
          )

          // 回读已确认时很快做一次完整 list 校正；若两次回读仍未确认，则稍后再校正，
          // 避免旧 lastPreset 立即把用户刚选中的标签顶回去。
          scheduleRemoteReload(confirmed ? 220 : 1200)
        } catch (error) {
          console.error('[PUBG][API] 保存最后使用标签失败:', error)
        }
      })

    lastPresetWriteQueue = writeTask.catch(() => undefined)
  }

  function schedulePointSync(item: ChijiPreset, sound: PositioningSound): void {
    if (!isLoggedIn()) return
    const requestGeneration = sourceGeneration.value
    const requestUuid = getUuid()
    const requestLinktype = getLinktype()
    const presetId = item.id
    const key = `${presetId}:${sound}`
    const previous = pointSyncTimers.get(key)
    if (previous) clearTimeout(previous)

    const timer = setTimeout(async () => {
      pointSyncTimers.delete(key)
      if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) return
      const addSucceeded = await awaitPendingAdd(presetId)
      if (!addSucceeded || requestGeneration !== sourceGeneration.value || !isLoggedIn()) return
      const resolvedId = resolvePresetId(presetId)
      const targetFids = sound === 'gunshots' ? GUNSHOT_FIDS : FOOTSTEP_FIDS
      const points = item.datapoint.filter(point => (targetFids as readonly number[]).includes(point.fid))

      try {
        console.log(`[PUBG][API] 同步${sound === 'footsteps' ? '脚步声' : '枪声'}滑动条`, {
          presetId: resolvedId,
          points: points.map(point => ({ fid: point.fid, freq: point.freq, gain: point.gain, q: point.q })),
        })
        const responses = await Promise.all(points.map(point => updatePubgPoint(requestUuid, resolvedId, point, requestLinktype)))
        if (responses.every(response => Number(response.code) === 1)) {
          scheduleRemoteReload(1400)
        } else {
          console.warn('[PUBG][API] 部分频点同步失败:', responses)
        }
      } catch (error) {
        console.error('[PUBG][API] 同步滑动条失败:', error)
      }
    }, API_POINT_DEBOUNCE_MS)

    pointSyncTimers.set(key, timer)
  }

  // 等待前一次一键吃鸡 0x14 / 曲线分包完成，避免与场景 0x72 抢 HID。
  function waitForDeviceQueue(): Promise<void> {
    return deviceQueue
  }

  async function setEnabled(value: boolean, options: { syncLastPreset?: boolean } = {}): Promise<void> {
    localActionGuardUntil = Date.now() + WRITE_GUARD_MS
    enabled.value = value
    hasEverOpened.value = hasEverOpened.value || value

    if (value) {
      const eqStore = await import('./eq').then(module => module.useEqStore())
      if (eqStore.isEnabled) {
        await eqStore.setEqEnabled(false)
        showGlobalLightTip('EQ settings have been turned off.')
      }

      appliedPresetId.value = currentPresetId.value
      if (options.syncLastPreset !== false) scheduleSetLastPreset(currentPresetId.value)
      await enqueueDevice(async () => {
        await sendProtocol()
        if (hidManager.isConnected()) await delay(30)
        await sendCurve(currentPreset.value, 'all')
      })
    } else {
      appliedPresetId.value = null
      await enqueueDevice(sendProtocol)
    }
  }

  async function selectPreset(id: string, options: { syncLastPreset?: boolean } = {}): Promise<void> {
    const resolvedId = resolvePresetId(id)
    if (!allPresets.value.some(item => item.id === resolvedId)) return

    localActionGuardUntil = Date.now() + WRITE_GUARD_MS
    currentPresetId.value = resolvedId
    markSelection()
    if (options.syncLastPreset !== false) scheduleSetLastPreset(resolvedId)

    if (!enabled.value) {
      await setEnabled(true, options)
      return
    }

    appliedPresetId.value = resolvedId
    await enqueueDevice(async () => {
      await sendProtocol()
      if (hidManager.isConnected()) await delay(30)
      await sendCurve(currentPreset.value, 'all')
    })
  }

  async function setLevel(key: PositioningSound, value: number): Promise<void> {
    if (!enabled.value || !currentPreset.value) return
    const item = currentPreset.value
    item.settings[key] = clampGain(value)
    updatePresetCurve(item)
    markMutation()

    await enqueueDevice(() => sendCurve(item, key))
    schedulePointSync(item, key)
  }

  const setGunLevel = (gain: number) => setLevel('gunshots', gain)
  const setFootstepLevel = (gain: number) => setLevel('footsteps', gain)

  async function commitAddedPreset(
    item: ChijiPreset,
    context: { generation: number; uuid: string; linktype: string },
  ): Promise<boolean> {
    if (!context.uuid) return true
    try {
      const response = await addPubgPreset(context.uuid, {
        id: item.id,
        name: item.name,
        datapoint: item.datapoint,
        isCustom: true,
        createdAt: item.createdAt,
      }, context.linktype)
      if (Number(response.code) !== 1) throw new Error(response.message || 'addEqData failed')
      if (context.generation !== sourceGeneration.value) return true

      // 当前后端通常沿用客户端提交 id；若明确返回了不同 id，则同步迁移引用。
      const data = response.data as unknown
      let returnedId = ''
      if (typeof data === 'string' || typeof data === 'number') returnedId = String(data)
      else if (data && typeof data === 'object') {
        const object = data as Record<string, unknown>
        const candidate = object.id ?? object.presetId ?? object.eqId
        if (typeof candidate === 'string' || typeof candidate === 'number') returnedId = String(candidate)
      }

      if (returnedId && returnedId !== item.id) {
        const oldId = item.id
        item.id = returnedId
        presetIdAliases.set(oldId, returnedId)
        orderedIds.value = orderedIds.value.map(id => id === oldId ? returnedId : id)
        if (currentPresetId.value === oldId) currentPresetId.value = returnedId
        if (appliedPresetId.value === oldId) appliedPresetId.value = returnedId
        if (pinnedPresetId.value === oldId) pinnedPresetId.value = returnedId
        console.log('[PUBG][API] addEqData 返回新 presetId，已迁移前端引用:', { oldId, returnedId })
      }

      scheduleRemoteReload(1400)
      if (currentPresetId.value === item.id) scheduleSetLastPreset(item.id)
      return true
    } catch (error) {
      console.error('[PUBG][API] 新增标签失败:', error)
      if (context.generation !== sourceGeneration.value) return false
      const index = customPresets.value.findIndex(preset => preset === item)
      if (index >= 0) customPresets.value.splice(index, 1)
      orderedIds.value = orderedIds.value.filter(id => id !== item.id)
      if (currentPresetId.value === item.id) currentPresetId.value = systemPreset.value.id
      if (appliedPresetId.value === item.id) appliedPresetId.value = enabled.value ? systemPreset.value.id : null
      showGlobalLightTip('Failed to save preset.')
      return false
    }
  }

  function addCustomPreset(name: string, base?: ChijiPreset): ChijiPreset | null {
    if (customPresets.value.length >= MAX_CUSTOM) return null
    const id = `custom-pubg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    const value = base ? cloneSettings(base.settings) : { footsteps: 0, gunshots: 0 }
    const item = createPreset(id, name.trim() || `Custom ${customPresets.value.length + 1}`, 'custom', value, false, Date.now())

    customPresets.value.push(item)
    orderedIds.value.push(id)
    currentPresetId.value = id
    markSelection()
    markMutation()

    if (isLoggedIn()) {
      const context = { generation: sourceGeneration.value, uuid: getUuid(), linktype: getLinktype() }
      const promise = commitAddedPreset(item, context).finally(() => pendingAddPromises.delete(id))
      pendingAddPromises.set(id, promise)
    }
    return item
  }

  async function renamePreset(id: string, name: string): Promise<boolean> {
    const normalizedName = name.trim().slice(0, 20)
    if (!normalizedName) return false

    const addSucceeded = await awaitPendingAdd(id)
    if (!addSucceeded) return false
    const resolvedId = resolvePresetId(id)
    const item = customPresets.value.find(preset => preset.id === resolvedId)
    if (!item) return false

    const duplicate = allPresets.value.some(preset => preset.id !== resolvedId && preset.name.trim().toLowerCase() === normalizedName.toLowerCase())
    if (duplicate) return false

    const previousName = item.name
    item.name = normalizedName
    markMutation()

    if (!isLoggedIn()) return true
    try {
      const response = await renamePubgPreset(getUuid(), resolvedId, normalizedName, getLinktype())
      if (Number(response.code) !== 1) throw new Error(response.message || 'updateName failed')
      scheduleRemoteReload(1400)
      return true
    } catch (error) {
      item.name = previousName
      console.error('[PUBG][API] 重命名标签失败:', error)
      return false
    }
  }

  async function deleteCustomPreset(id: string): Promise<boolean> {
    const addSucceeded = await awaitPendingAdd(id)
    if (!addSucceeded) return false
    const resolvedId = resolvePresetId(id)
    const index = customPresets.value.findIndex(item => item.id === resolvedId)
    if (index < 0) return false

    const removed = customPresets.value[index]
    const oldOrderedIds = [...orderedIds.value]
    const oldPinned = pinnedPresetId.value
    const oldCurrent = currentPresetId.value
    const oldApplied = appliedPresetId.value

    customPresets.value.splice(index, 1)
    orderedIds.value = orderedIds.value.filter(item => item !== resolvedId)
    if (pinnedPresetId.value === resolvedId) pinnedPresetId.value = null

    // 删除当前标签后优先应用最新的剩余自定义标签；只有没有自定义标签时才回到 Preset。
    // customPresets 尾项保持新增顺序中的最新标签，不受置顶 orderedIds 的显示排序影响。
    const latestRemainingPreset = customPresets.value[customPresets.value.length - 1] || systemPreset.value
    if (currentPresetId.value === resolvedId) currentPresetId.value = latestRemainingPreset.id
    if (appliedPresetId.value === resolvedId) appliedPresetId.value = enabled.value ? latestRemainingPreset.id : null
    markMutation()
    markSelection()

    if (enabled.value && oldApplied === resolvedId) {
      void enqueueDevice(async () => {
        await sendProtocol()
        if (hidManager.isConnected()) await delay(30)
        await sendCurve(latestRemainingPreset, 'all')
      })
      scheduleSetLastPreset(latestRemainingPreset.id)
    }

    if (!isLoggedIn()) return true
    try {
      const response = await removePubgPreset(getUuid(), resolvedId, getLinktype())
      if (Number(response.code) !== 1) throw new Error(response.message || 'deleteEqData failed')
      scheduleRemoteReload(1400)
      return true
    } catch (error) {
      customPresets.value.splice(Math.max(0, Math.min(index, customPresets.value.length)), 0, removed)
      orderedIds.value = oldOrderedIds
      pinnedPresetId.value = oldPinned
      currentPresetId.value = oldCurrent
      appliedPresetId.value = oldApplied
      console.error('[PUBG][API] 删除标签失败:', error)
      return false
    }
  }

  async function resetPreset(id = currentPresetId.value): Promise<boolean> {
    const resolvedId = resolvePresetId(id)
    const item = allPresets.value.find(preset => preset.id === resolvedId)
    if (!item) return false

    markMutation()
    if (isLoggedIn()) {
      try {
        const response = await resetPubgPreset(getUuid(), resolvedId, getLinktype())
        if (Number(response.code) !== 1) throw new Error(response.message || 'reset failed')

        // 重置后的真实曲线继续从服务器读取，避免把本地默认值当成云端真值。
        const detail = await findPubgById(getUuid(), resolvedId, getLinktype())
        if (Number(detail.code) === 1 && detail.data) {
          const normalized = normalizeRemotePreset(detail.data, item.type, item.isPinned)
          item.name = normalized.name
          item.settings = normalized.settings
          item.datapoint = normalized.datapoint
          if (item.type === 'system') systemOriginSettings.value = cloneSettings(normalized.settings)
        } else {
          item.settings = cloneSettings(item.type === 'system' ? systemOriginSettings.value : { footsteps: 0, gunshots: 0 })
          updatePresetCurve(item)
        }
        scheduleRemoteReload(1400)
      } catch (error) {
        console.error('[PUBG][API] 重置标签失败:', error)
        return false
      }
    } else {
      item.settings = cloneSettings(item.type === 'system' ? systemOriginSettings.value : { footsteps: 0, gunshots: 0 })
      updatePresetCurve(item)
    }

    if (enabled.value && currentPresetId.value === resolvedId) {
      await enqueueDevice(() => sendCurve(item, 'all'))
    }
    return true
  }

  async function pinCurrentPreset(): Promise<boolean> {
    const item = currentPreset.value
    if (!item) return false
    const resolvedId = resolvePresetId(item.id)
    const oldPinned = pinnedPresetId.value
    const oldOrder = [...orderedIds.value]

    pinnedPresetId.value = resolvedId
    orderedIds.value = [resolvedId, ...orderedIds.value.filter(id => id !== resolvedId)]
    applyPinnedFlags()
    markMutation()

    if (enabled.value) void enqueueDevice(sendProtocol)
    if (!isLoggedIn()) return true

    try {
      const response = await pinPubgPreset(getUuid(), resolvedId, getLinktype())
      if (Number(response.code) !== 1) throw new Error(response.message || 'setTop failed')
      scheduleRemoteReload(1400)
      return true
    } catch (error) {
      pinnedPresetId.value = oldPinned
      orderedIds.value = oldOrder
      applyPinnedFlags()
      console.error('[PUBG][API] 置顶标签失败:', error)
      return false
    }
  }

  async function loadFromRemote(force = false): Promise<boolean> {
    if (!isLoggedIn() || (remoteLoaded.value && !force) || remoteLoading.value) return false

    const requestGeneration = sourceGeneration.value
    const requestSelectionVersion = selectionVersion.value
    const requestMutationVersion = mutationVersion.value
    const requestUuid = getUuid()
    const requestLinktype = getLinktype()
    remoteLoading.value = true

    try {
      const response = await getPubgList(requestUuid, requestLinktype)
      if (requestGeneration !== sourceGeneration.value || !isLoggedIn()) {
        console.log('[PUBG][SOURCE] 丢弃旧上下文 list 响应')
        return false
      }
      if (Number(response.code) !== 1 || !response.data || !Array.isArray(response.data.presets)) {
        console.warn('[PUBG][API] list 数据无效:', response)
        return false
      }

      // 请求期间如果发生新增/删除/重命名/滑动条写入，这个 list 很可能是旧快照，不能覆盖用户新操作。
      if (requestMutationVersion !== mutationVersion.value) {
        console.log('[PUBG][SOURCE] list 返回期间存在本地写操作，保留最新本地操作并等待下一次服务器校正')
        scheduleRemoteReload(1400)
        return false
      }

      const rawPresets = response.data.presets
      if (!rawPresets.length) {
        console.warn('[PUBG][API] list 返回空预设列表')
        return false
      }

      const systemIndex = detectSystemPresetIndex(rawPresets)
      const nextSystemSource = rawPresets[systemIndex]
      const nextSystem = normalizeRemotePreset(nextSystemSource, 'system', response.data.defaultPreset === String(nextSystemSource.id))
      const nextCustoms = rawPresets
        .filter((_, index) => index !== systemIndex)
        .map((preset, index) => normalizeRemotePreset(
          { ...preset, createdAt: preset.createdAt || Date.now() + index },
          'custom',
          response.data.defaultPreset === String(preset.id),
        ))

      const remoteIds = rawPresets.map(item => String(item.id))
      const pinned = String(response.data.defaultPreset || '')
      let nextOrder = remoteIds.filter((id, index) => remoteIds.indexOf(id) === index)
      if (pinned && nextOrder.includes(pinned)) nextOrder = [pinned, ...nextOrder.filter(id => id !== pinned)]

      const userSelectedWhileLoading = selectionVersion.value !== requestSelectionVersion
      const preservedCurrent = resolvePresetId(currentPresetId.value)
      const allNextIds = new Set([nextSystem.id, ...nextCustoms.map(item => item.id)])
      const serverLast = String(response.data.lastPreset || '')

      systemPreset.value = nextSystem
      customPresets.value = nextCustoms
      systemOriginSettings.value = cloneSettings(nextSystem.settings)
      pinnedPresetId.value = pinned && allNextIds.has(pinned) ? pinned : null
      orderedIds.value = nextOrder.filter(id => allNextIds.has(id))
      if (!orderedIds.value.includes(nextSystem.id)) orderedIds.value.unshift(nextSystem.id)
      nextCustoms.forEach(item => { if (!orderedIds.value.includes(item.id)) orderedIds.value.push(item.id) })
      applyPinnedFlags()

      if (userSelectedWhileLoading && allNextIds.has(preservedCurrent)) {
        currentPresetId.value = preservedCurrent
        console.log('[PUBG][SOURCE] list 完成，但用户加载期间已点击新标签，保留用户最新选择:', preservedCurrent)
      } else if (serverLast && allNextIds.has(serverLast)) {
        currentPresetId.value = serverLast
      } else if (!allNextIds.has(currentPresetId.value)) {
        currentPresetId.value = nextSystem.id
      }

      appliedPresetId.value = enabled.value ? currentPresetId.value : null
      remoteLoaded.value = true

      console.log('[PUBG][SOURCE] 已登录：标签/两个滑动条已切换为服务器数据源', {
        uuid: requestUuid,
        linktype: requestLinktype,
        enabledFromFirst0x80: enabled.value,
        currentPresetId: currentPresetId.value,
        lastPreset: response.data.lastPreset,
        pinnedPreset: pinnedPresetId.value,
        presets: allPresets.value.map(item => ({
          id: item.id,
          name: item.name,
          type: item.type,
          footsteps: item.settings.footsteps,
          gunshots: item.settings.gunshots,
        })),
      })

      // 已经从第一次 0x80 得知 Positioning 开启时，用服务器标签/曲线重新校正设备。
      if (loggedInHardwareEnabledCaptured && enabled.value) {
        void enqueueDevice(async () => {
          await sendProtocol()
          if (hidManager.isConnected()) await delay(30)
          await sendCurve(currentPreset.value, 'all')
        })
      }
      return true
    } catch (error) {
      console.error('[PUBG][API] 加载服务器预设失败:', error)
      return false
    } finally {
      if (requestGeneration === sourceGeneration.value) remoteLoading.value = false
    }
  }

  function syncHardwareStatus(value: { enabled: boolean; tag: number }): void {
    if (Date.now() < localActionGuardUntil) {
      console.debug('[PUBG][SYNC] 本地写入保护期，忽略本次旧硬件回读:', value)
      return
    }

    const normalizedEnabled = !!value.enabled
    const normalizedTag = Number(value.tag) || 0
    const hardwareSyncKey = `${sourceContextKey}|${normalizedEnabled ? 1 : 0}|${normalizedTag}`

    // 轮询 0x80 每 2 秒都会返回一次。值没有变化时不重复写页面，也不重复刷日志。
    if (hardwareSyncKey === lastHardwareSyncKey) return
    lastHardwareSyncKey = hardwareSyncKey

    if (isLoggedIn()) {
      // 登录态仅使用当前账号 + 当前连接方式第一次 0x80 的开关值。
      // 标签和两个滑动条全部来自 /pubg/list，后续旧硬件 chickenTag 不再覆盖页面。
      if (loggedInHardwareEnabledCaptured) return
      loggedInHardwareEnabledCaptured = true
      enabled.value = normalizedEnabled
      hasEverOpened.value = hasEverOpened.value || normalizedEnabled
      appliedPresetId.value = normalizedEnabled ? currentPresetId.value : null
      console.log('[PUBG][SYNC] 已登录：仅采纳第一次 Positioning 开关', {
        enabled: normalizedEnabled,
        ignoredHardwareTag: normalizedTag,
        currentServerPreset: currentPresetId.value,
      })

      if (normalizedEnabled && remoteLoaded.value) {
        void enqueueDevice(async () => {
          await sendProtocol()
          if (hidManager.isConnected()) await delay(30)
          await sendCurve(currentPreset.value, 'all')
        })
      }
      return
    }

    // 未登录：继续实时同步硬件保存的 chickenOneKey / chickenTag。
    enabled.value = normalizedEnabled
    hasEverOpened.value = hasEverOpened.value || normalizedEnabled
    const presets = allPresets.value
    const index = Math.max(0, Math.min(Math.max(0, presets.length - 1), normalizedTag))
    const id = presets[index]?.id || systemPreset.value.id
    currentPresetId.value = id
    appliedPresetId.value = normalizedEnabled ? id : null
    console.log('[PUBG][SYNC] 未登录：硬件状态发生变化', { enabled: normalizedEnabled, tag: normalizedTag, presetId: id })
  }

  function syncLatestHardwareForCurrentContext(): void {
    const status = deviceStore.deviceStatus
    if (!status) return
    const useDongle = deviceStore.connectionType === '2.4G'
    const chijiEnabled = useDongle ? status.dongleChickenOneKey : status.usbChickenOneKey
    const chijiTag = useDongle ? status.dongleChickenTag : status.usbChickenTag
    const rawByte = Number(status.chickenByte ?? 0)
    syncHardwareStatus({
      enabled: Number(chijiEnabled ?? status.chickenOneKey ?? 0) === 1,
      tag: Number(chijiTag ?? status.chickenTag ?? (rawByte & 0x7F)) || 0,
    })
  }

  function clearTimers(): void {
    if (remoteReloadTimer) {
      clearTimeout(remoteReloadTimer)
      remoteReloadTimer = null
    }
    pointSyncTimers.forEach(timer => clearTimeout(timer))
    pointSyncTimers.clear()
  }

  function resetSourceData(): void {
    clearTimers()
    enabled.value = false
    hasEverOpened.value = false
    currentPresetId.value = 'initial'
    appliedPresetId.value = null
    pinnedPresetId.value = null
    customPresets.value = []
    systemPreset.value = createPreset('initial', 'Preset', 'system', DEFAULT_SYSTEM_SETTINGS)
    systemOriginSettings.value = cloneSettings(DEFAULT_SYSTEM_SETTINGS)
    orderedIds.value = ['initial']
    remoteLoaded.value = false
    remoteLoading.value = false
    loggedInHardwareEnabledCaptured = false
    lastHardwareSyncKey = ''
    localActionGuardUntil = 0
    pendingAddPromises.clear()
    presetIdAliases.clear()
    selectionVersion.value = 0
    mutationVersion.value = 0
  }

  function handleSourceContextChange(): void {
    const connection = deviceStore.connectionType || hidManager.getConnectionType() || 'unknown'
    const nextKey = isLoggedIn()
      ? `user:${getUuid()}:${connection}`
      : `guest:${connection}`

    if (nextKey === sourceContextKey) return
    sourceContextKey = nextKey
    sourceGeneration.value += 1
    resetSourceData()

    console.log('[PUBG][SOURCE] 数据源上下文切换', {
      context: nextKey,
      loggedIn: isLoggedIn(),
      linktype: getLinktype(),
      source: isLoggedIn() ? 'server(/pubg/*) + first 0x80 switch' : 'hardware chickenOneKey/chickenTag',
    })

    // 切账号、登录/退出、USB/2.4G 切换后，都重新读取当前链路的最新 0x80。
    syncLatestHardwareForCurrentContext()
    if (isLoggedIn()) void loadFromRemote(true)
    // 0x13 只在用户实际操作吃鸡功能、需要下发 0x14/0x15~0x18 时发送。
    // 登录/退出、切账号、USB/2.4G 切换仅切换数据源，不主动下发 0x13。
  }

  function loadFromStorage(): void {
    // 兼容页面原来的调用入口。登录态标签/曲线不再从 localStorage 取，统一走服务器。
    handleSourceContextChange()
    if (isLoggedIn() && !remoteLoaded.value && !remoteLoading.value) void loadFromRemote(true)
  }

  watch(
    () => [userStore.isLoggedIn, userStore.uuid, deviceStore.connectionType] as const,
    () => handleSourceContextChange(),
    { immediate: true },
  )

  return {
    enabled,
    hasEverOpened,
    currentPresetId,
    appliedPresetId,
    pinnedPresetId,
    customPresets,
    allPresets,
    currentPreset,
    isCustomPreset,
    tagValue,
    remoteLoaded,
    remoteLoading,
    maxCustomPresets: MAX_CUSTOM,
    currentPresetSettings: computed(() => currentPreset.value?.settings),
    waitForDeviceQueue,
    setEnabled,
    selectPreset,
    setGunLevel,
    setFootstepLevel,
    addCustomPreset,
    renamePreset,
    deleteCustomPreset,
    resetPreset,
    pinCurrentPreset,
    loadFromStorage,
    saveToStorage: () => undefined,
    applyCurrentPreset: () => sendCurve(currentPreset.value, 'all'),
    loadFromRemote,
    syncHardwareStatus,
  }
})
